import {mapObject} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {Batch, BatchSummary, Container, ContainerInfo} from 'constants/SampleConstants';
import {BatchInfo, Bioentity, BioentityInfo, SchemaTag} from 'folder-items/FileConstants';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {TableWithoutColumnWidths} from 'notebook/simple-tables/ManagedColumnsSimpleTableEditable';
import {
  FulfillableStatusColumnDefinition,
  RequestTaskSelectionColumnDefinition,
  SampleGroupSampleColumnDefinition,
  schemaFieldToColumnDefinition,
  WithMaybeLinkedSampleColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  BaseTypedValueType,
  Cells,
  ColumnConstraint,
  PLACEHOLDER_CELL,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import {BooleanTypedValue, FulfillableStatusTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {isBioentityOrBioentityInfo} from 'registry/BioentityPredicates';
import {FulfillmentStatus} from 'requests/fulfillments/FulfillmentConstants';
import {RequestFullfillableStatuses} from 'requests/RequestConstants';
import {
  getGridPositionTableCell,
  getSampleGroupSampleTableCell,
  getStorableTableCell,
} from 'requests/RequestTableCells';
import {
  getContainerColumnsForSampleGroupField,
  getContainerForSample,
  getContainerGridCell,
  getContainerLocation,
  getSampleForField,
} from 'requests/SampleGroupUtil';
import {UIRequestTask} from 'requests/tasks/RequestTaskConstants';
import * as TagUtil from 'schemas/tags/TagUtil';
import {Optional} from 'util/Types';
import makeSample from 'workflows/samples/makeSample';
import {
  PreparedSample,
  PreparedSampleGroup,
  PreparedUISampleGroup,
} from 'workflows/samples/PreparedSampleGroupConstants';
import {BaseSampleGroup, SampleGroup, UISampleGroup} from 'workflows/WorkflowConstants';

import {ContainerGraphQL} from '../../../graphql-fragments/inventory/__generated/ContainerGraphQL';
import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {RequestTaskGraphQL} from '../../../graphql-fragments/requests/__generated/RequestTaskGraphQL';
import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import {SampleGroupSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/SampleGroupSchemaGraphQL';

function isPersistedTask(task: {id?: string; cid?: string}): task is RequestTaskGraphQL {
  return task.hasOwnProperty('id');
}

function isPersistedSampleGroup(sampleGroup: {
  id?: string;
  cid?: string;
}): sampleGroup is PreparedSampleGroup {
  return sampleGroup.hasOwnProperty('id');
}

export function getSelectedRequestTaskKeyMap({
  requestTasks,
  sampleGroup,
  sampleGroupIdsByRequestTaskId,
}: {
  requestTasks: Array<RequestTaskGraphQL | UIRequestTask>;
  sampleGroup: PreparedSampleGroup | UISampleGroup;
  sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
}): {[taskKey: string]: boolean} {
  return _.object(
    requestTasks.map((requestTask) => [
      getRequestTaskKey(requestTask),
      Boolean(
        sampleGroupIdsByRequestTaskId[getRequestTaskKey(requestTask)].includes(getSampleGroupKey(sampleGroup))
      ),
    ])
  );
}

export function getRequestTaskKey(requestTask: RequestTaskGraphQL | UIRequestTask): string {
  return isPersistedTask(requestTask) ? requestTask.id : requestTask.cid;
}

function getRequestTaskName(
  requestTask: RequestTaskGraphQL | UIRequestTask,
  requestTaskSchemasById: {[id: string]: RequestTaskSchemaGraphQL}
): string {
  const schemaId = isPersistedTask(requestTask) ? requestTask.schema.id : requestTask.schemaId;
  return requestTaskSchemasById[schemaId].name;
}

export function getSampleGroupKey(
  sampleGroup: PreparedSampleGroup | UISampleGroup | PreparedUISampleGroup
): string {
  return isPersistedSampleGroup(sampleGroup) ? sampleGroup.id : sampleGroup.cid;
}

export function makeStageRunSample(
  sample: Bioentity | BioentityInfo | Batch | BatchInfo,
  container: ContainerInfo | null
): PreparedSample {
  // `id` is nullable in BatchInfo, but not in BatchSummary. If `id` is not present, return null. Otherwise
  // make `id` non-null to be compatible with BatchSummary.
  const batchSample: Optional<BatchSummary> =
    !isBioentityOrBioentityInfo(sample) && sample.id != null ? {...sample, id: sample.id} : null;
  return makeSample(
    {
      containerId: container && container.id,
      ...(sample.id != null && isBioentityOrBioentityInfo(sample)
        ? {fileId: sample.id}
        : {batch: batchSample}),
    },
    /* id */ null,
    sample.name,
    // Bioentities have `editURL` and batches `url`
    (isBioentityOrBioentityInfo(sample) ? sample.editURL : sample.url) || null
  );
}

export function updateSampleGroupWithSample<T extends BaseSampleGroup>(
  sampleGroupSchema: SampleGroupSchemaGraphQL,
  sampleGroup: T,
  fieldName: string,
  sample: PreparedSample | null
): T {
  const tagIndex = sampleGroup.tags.findIndex((tag) => tag.name === fieldName);

  const newTargetSamples = sample ? [sample] : [];

  let newTags;
  // Tags may be sparse
  if (tagIndex === -1) {
    const schemaField = sampleGroupSchema.fieldsJson.find(
      (field: {name: string}) => field.name === fieldName
    );
    assertAndContinue(
      Boolean(schemaField),
      `Expected to find matching field for fieldName in sample group schema"`
    );
    const newTag = {
      ...TagUtil.getInitialTag(schemaField),
      targetSamples: newTargetSamples,
    } as SchemaTag;
    newTags = reactUpdate(sampleGroup.tags, {$push: [newTag]});
  } else {
    const newTag = {
      ...sampleGroup.tags[tagIndex],
      targetSamples: newTargetSamples,
    } as SchemaTag;
    newTags = reactUpdate(sampleGroup.tags, {[tagIndex]: {$set: newTag}});
  }

  return {
    ...sampleGroup,
    tags: newTags,
  };
}

function isRequestedExecution(
  selectedRequestTaskIdMap: {[taskId: string]: boolean},
  requestTask: RequestTaskGraphQL | UIRequestTask
): boolean {
  return selectedRequestTaskIdMap[getRequestTaskKey(requestTask)];
}

export function _getFulfillableOrSelectedStatus(status: string | null, selected: boolean): string | null {
  // We fallback to the selected status (if a request / sampleGroup pair has a checkbox) to handle
  // changes a user makes with the tab open. A request / sampleGroup pair may be selected without
  // being in the status list if the user just made an edit to the RequestSampleTable - we assume
  // the fulfillable is in a PENDING state. If there is a status but the fulfillable is *not* selected,
  // we assume it has just been deselected and return null.
  if (status && selected) {
    return status;
  } else if (selected) {
    return FulfillmentStatus.PENDING;
  } else {
    return null;
  }
}

function getFulfillableStatus(
  fulfillableStatuses: RequestFullfillableStatuses,
  sampleGroup: SampleGroup,
  requestTask: RequestTaskGraphQL
): FulfillmentStatus | null {
  const taskStatuses = fulfillableStatuses[sampleGroup.id];
  if (taskStatuses == null) {
    return null;
  }
  return taskStatuses[requestTask.id];
}

function getContainerForSampleFromGQL(
  sample: PreparedSample,
  containersById: {
    [containerId: string]: ContainerGraphQL | null;
  }
): Container | null {
  const container = getContainerForSample({sample, containersById});
  return container ? graphQLToREST('Container', container as ContainerGraphQL) : null;
}

function getTableCells({
  sampleGroups,
  requestTasks,
  sampleGroupIdsByRequestTaskId,
  isReadOnly,
  fulfillableStatuses,
  containersById,
  ancestralStorablesById,
  columnConstraints,
}: {
  sampleGroups: Array<PreparedSampleGroup | PreparedUISampleGroup>;
  requestTasks: Array<RequestTaskGraphQL | UIRequestTask>;
  sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
  isReadOnly: boolean;
  fulfillableStatuses: RequestFullfillableStatuses;
  containersById: {[containerId: string]: ContainerGraphQL | null};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
  columnConstraints: Array<ColumnConstraint>;
}): Cells {
  if (sampleGroups.length === 0) {
    return [
      columnConstraints.map((constraint) => ({
        ...PLACEHOLDER_CELL,
        locked: true,
      })),
    ];
  }

  return sampleGroups.map((sampleGroup) => {
    const selectedRequestTaskIdMap = getSelectedRequestTaskKeyMap({
      requestTasks,
      // @ts-expect-error
      sampleGroup,
      sampleGroupIdsByRequestTaskId,
    });

    return columnConstraints.map((constraint) => {
      const columnDefinitionType = constraint.columnDefinition!.type;
      if (columnDefinitionType === TableColumnType.SAMPLE_GROUP_SAMPLE) {
        const sample = getSampleForField(sampleGroup, constraint.columnDefinition.schemaField);
        return getSampleGroupSampleTableCell(sample);
      }
      if (
        [TableColumnType.CONTAINERS, TableColumnType.PARENT_STORABLE, TableColumnType.POSITION].includes(
          columnDefinitionType
        )
      ) {
        const linkedSampleSchemaField = (constraint.columnDefinition as WithMaybeLinkedSampleColumnDefinition)
          .linkedSampleSchemaField;
        const sample = linkedSampleSchemaField
          ? getSampleForField(sampleGroup, linkedSampleSchemaField)
          : null;
        if (sample == null) {
          return {text: '', locked: true};
        }
        if (columnDefinitionType === TableColumnType.CONTAINERS) {
          // The Cell returned here will be rendered either as a chip or as "(No access)", based
          // on whether or not the container info is in the redux store.
          return getStorableTableCell(
            getContainerForSampleFromGQL(sample, containersById),
            sample.containerId
          );
        }
        if (columnDefinitionType === TableColumnType.PARENT_STORABLE) {
          const container = getContainerForSampleFromGQL(sample, containersById);
          const location = getContainerLocation({
            ancestralStorablesById,
            container,
          });
          return getStorableTableCell(location);
        }
        if (columnDefinitionType === TableColumnType.POSITION) {
          const gridCell = getContainerGridCell({sample, containersById});
          return getGridPositionTableCell(gridCell);
        }
      }
      if (columnDefinitionType === TableColumnType.REQUEST_TASK_SELECTION) {
        return {
          text: '',
          typedValue: {
            type: BaseTypedValueType.BOOLEAN,
            booleanValue: isRequestedExecution(
              selectedRequestTaskIdMap,
              constraint.columnDefinition.requestTask
            ),
          } as BooleanTypedValue,
          locked: isReadOnly,
        };
      }
      if (columnDefinitionType === TableColumnType.FULFILLABLE_STATUS) {
        const status = _getFulfillableOrSelectedStatus(
          getFulfillableStatus(
            fulfillableStatuses,
            sampleGroup as SampleGroup,
            constraint.columnDefinition.requestTask as RequestTaskGraphQL
          ),
          isRequestedExecution(selectedRequestTaskIdMap, constraint.columnDefinition.requestTask)
        );
        return {
          text: '',
          typedValue: {
            type: BaseTypedValueType.FULFILLABLE_STATUS,
            status,
          } as FulfillableStatusTypedValue,
          locked: isReadOnly,
        };
      }

      assertAndContinue(false, 'Unexpected column constraint', {columnConstraint: constraint});
      return {text: ''};
    });
  });
}

export function getColumnConstraints({
  sampleGroupSchema,
  requestTasks,
  sampleGroups,
  containersById,
  requestTaskSchemasById,
  isReadOnly,
  ancestralStorablesById,
}: {
  sampleGroupSchema: SampleGroupSchemaGraphQL;
  requestTasks: Array<RequestTaskGraphQL | UIRequestTask>;
  sampleGroups: Array<PreparedSampleGroup | PreparedUISampleGroup>;
  containersById: {[containerId: string]: ContainerGraphQL | null};
  requestTaskSchemasById: {[taskSchemaId: string]: RequestTaskSchemaGraphQL};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
  isReadOnly: boolean;
}): Array<ColumnConstraint> {
  let sampleColumns: Array<ColumnConstraint> = [];
  for (const sampleGroupField of sampleGroupSchema.fieldsJson) {
    const columnDefinition: SampleGroupSampleColumnDefinition = {
      ...schemaFieldToColumnDefinition(sampleGroupField),
      type: TableColumnType.SAMPLE_GROUP_SAMPLE,
    };
    sampleColumns.push({
      columnDefinition,
      renderedLabel: sampleGroupField.displayName,
    });
    sampleColumns = [
      ...sampleColumns,
      ...getContainerColumnsForSampleGroupField({
        sampleGroups,
        sampleGroupField,
        containersById: mapObject(containersById, (container) =>
          container != null ? graphQLToREST('Container', container) : container
        ),
        ancestralStorablesById,
      }),
    ];
  }

  const taskColumns = requestTasks.map((requestTask) => {
    if (sampleGroups.length === 0) {
      return {
        renderedLabel: getRequestTaskName(requestTask, requestTaskSchemasById),
      };
    } else if (isReadOnly) {
      const columnDefinition: FulfillableStatusColumnDefinition = {
        type: TableColumnType.FULFILLABLE_STATUS,
        isRequired: false,
        requestTask,
      };
      return {
        columnDefinition,
        renderedLabel: getRequestTaskName(requestTask, requestTaskSchemasById),
      };
    } else {
      const columnDefinition: RequestTaskSelectionColumnDefinition = {
        type: TableColumnType.REQUEST_TASK_SELECTION,
        isRequired: false,
        requestTask,
      };
      return {
        columnDefinition,
        renderedLabel: getRequestTaskName(requestTask, requestTaskSchemasById),
      };
    }
  });
  return [...sampleColumns, ...taskColumns];
}

export function getColumnWidths({
  columnConstraints,
}: {
  columnConstraints: Array<ColumnConstraint>;
}): Array<number> {
  return columnConstraints.map((constraint) => {
    if (constraint.columnDefinition == null) {
      return 100;
    }
    if (
      [
        TableColumnType.SAMPLE_GROUP_SAMPLE,
        TableColumnType.CONTAINERS,
        TableColumnType.PARENT_STORABLE,
      ].includes(constraint.columnDefinition.type)
    ) {
      return 120;
    }
    if (
      [
        TableColumnType.POSITION,
        TableColumnType.REQUEST_TASK_SELECTION,
        TableColumnType.FULFILLABLE_STATUS,
      ].includes(constraint.columnDefinition.type)
    ) {
      return 100;
    }
    assertAndContinue(false, 'Unexpected column constraint', {columnConstraint: constraint});
    return 100;
  });
}

export function convertToTable({
  sampleGroups,
  sampleGroupSchema,
  requestTasks,
  sampleGroupIdsByRequestTaskId,
  requestTaskSchemasById,
  containersById,
  ancestralStorablesById,
  isReadOnly,
  fulfillableStatuses,
}: {
  sampleGroups: Array<PreparedSampleGroup | PreparedUISampleGroup>;
  sampleGroupSchema: SampleGroupSchemaGraphQL;
  requestTasks: Array<RequestTaskGraphQL | UIRequestTask>;
  sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
  requestTaskSchemasById: {[taskSchemaId: string]: RequestTaskSchemaGraphQL};
  containersById: {[containerId: string]: ContainerGraphQL | null};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
  isReadOnly: boolean;
  fulfillableStatuses: RequestFullfillableStatuses;
}): TableWithoutColumnWidths {
  const columnConstraints = getColumnConstraints({
    sampleGroupSchema,
    requestTasks,
    sampleGroups,
    containersById,
    requestTaskSchemasById,
    ancestralStorablesById,
    isReadOnly,
  });

  return {
    name: '',
    table: getTableCells({
      sampleGroups,
      requestTasks,
      sampleGroupIdsByRequestTaskId,
      containersById,
      ancestralStorablesById,
      columnConstraints,
      isReadOnly,
      fulfillableStatuses,
    }),
    columnConstraints,
    rowData: sampleGroups.map((sampleGroup) => ({sampleGroupId: getSampleGroupKey(sampleGroup)})),
    headerRow: false,
    headerCol: false,
    columnLabels: columnConstraints.map((constraint) => constraint.renderedLabel as string),
    _hideRename: true,
    allowRowRemoval: true,
    allowNoRows: true,
    lockedDims: true,
  };
}
